import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Ban, Clock } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Customer-facing calendar: only dates the cleaner published are selectable.
export default function BookingCalendar({ cleanerId, durationHours, date, startTime, onPick }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const from = iso(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    const to = iso(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));
    api.get(`/availability/cleaner/${cleanerId}`, { params: { from_date: from, to_date: to, duration_hours: durationHours } })
      .then((r) => setData(r.data))
      .catch(() => setData({ days: [] }))
      .finally(() => setLoading(false));
  }, [cleanerId, durationHours, cursor]);

  const byDate = useMemo(() => Object.fromEntries((data?.days || []).map((d) => [d.date, d])), [data]);
  const cells = useMemo(() => {    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: total }, (_, i) =>
      new Date(cursor.getFullYear(), cursor.getMonth(), i + 1))];
  }, [cursor]);

  const chosen = date ? byDate[date] : null;

  return (
    <div className="space-y-4" data-testid="booking-calendar">
      <div className="bg-card rounded-2xl border border-primary/5 p-4">
        <div className="flex items-center justify-between">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="p-2 rounded-full hover:bg-secondary" aria-label="Previous month" data-testid="bookcal-prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="font-display font-semibold" data-testid="bookcal-month">
            {cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
          </p>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="p-2 rounded-full hover:bg-secondary" aria-label="Next month" data-testid="bookcal-next">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Available</span>
          <span className="inline-flex items-center gap-1.5"><Ban className="h-3.5 w-3.5" /> Unavailable — not selectable</span>
        </div>

        <div className="grid grid-cols-7 gap-1 mt-3 text-center text-xs text-muted-foreground">
          {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1 mt-1">
          {cells.map((d, i) => {
            if (!d) return <span key={`x${i}`} />;
            const key = iso(d);
            const day = byDate[key];
            const open = !!day?.available;
            const isChosen = date === key;
            const unknown = loading && !day;
            return (
              <button key={key} onClick={() => open && onPick(key, null)} disabled={!open}
                aria-disabled={!open}
                aria-label={`${d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })} — ${unknown ? "checking availability" : open ? "available" : "unavailable"}`}
                title={unknown ? "Checking availability" : open ? "Available" : "Unavailable"}
                className={`aspect-square rounded-xl text-sm flex flex-col items-center justify-center border-2 transition-colors
                  ${isChosen ? "border-accent bg-accent text-white"
                    : unknown ? "border-border bg-secondary/30 text-muted-foreground animate-pulse"
                      : open ? "border-emerald-400 bg-emerald-50 text-emerald-900 hover:border-emerald-600"
                        : "border-border bg-secondary/40 text-muted-foreground cursor-not-allowed opacity-60"}`}
                data-testid={`bookcal-day-${key}`}>
                <span className="font-medium">{d.getDate()}</span>
                {unknown ? null
                  : open ? <CheckCircle2 className={`h-3 w-3 ${isChosen ? "text-white" : "text-emerald-600"}`} />
                    : <Ban className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
        {loading && <p className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking availability…</p>}
        {!loading && data && !data.has_published_availability && (
          <p className="text-sm text-muted-foreground mt-3" data-testid="bookcal-no-availability">
            This cleaner hasn't published availability for this month. Try the next month or choose another cleaner.
          </p>
        )}
      </div>

      {date && (
        <div className="bg-card rounded-2xl border border-primary/5 p-4" data-testid="booking-times">
          <p className="text-sm font-semibold inline-flex items-center gap-2">
            <Clock className="h-4 w-4 text-accent" /> Available times on{" "}
            {new Date(date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          {chosen?.start_times?.length ? (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
              {chosen.start_times.map((t) => (
                <button key={t} onClick={() => onPick(date, t)}
                  className={`py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${startTime === t ? "border-accent bg-accent text-white" : "border-border hover:border-accent/50"}`}
                  data-testid={`booktime-${t}`}>{t}</button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-2" data-testid="booking-times-empty">
              No {durationHours}-hour slot is left on this date. Try a shorter booking or another date.
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            Times shown come from {data?.provider_label === "Cleaning Company" ? "the company's" : "the cleaner's"} own
            calendar and already exclude their existing jobs. You can only book a time they've confirmed.
          </p>
        </div>
      )}
    </div>
  );
}
